//React
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
//Material UI
import CheckCircleIcon from "../../assets/images/CheckCircle.svg";
import WarningCircleIcon from "../../assets/images/WarningCircle.svg";
import AbnormalCircleIcon from "../../assets/images/AbnormalCircle.svg";
import ListIcon from "../../assets/images/ListIcon.svg";
import ResultCalendarMonth from "../../assets/images/ResultCalendarMonth.svg";
import BackArrow from "../../assets/images/BackArrow.svg";
//API
import { PatientApi } from "../../api";
//CSS
import "./ResultPage.css";
import Header from "../../components/Header/Header";
import PatientList from "../../components/PatientList/PatientList";
import AccountCircleIcon from "../../assets/images/AccountCircle.svg";
import Footer from "../../components/Footer/Footer";
import SecureStorage from "react-secure-storage";

// 진단 결과 아이템 컴포넌트
const DiagnosisResultItem = ({
  position,
  diagnosisData,
  result,
  setResult,
  formatSurveyResult,
  formatDiagnosisWithLineBreaks,
}) => {
  const [diagnosis, setDiagnosis] = useState(
    diagnosisData?.diagnosisList?.[0]?.finalDiagnosis || ""
  );
  const [aiDiagnosis, setAiDiagnosis] = useState(
    diagnosisData?.diagnosisList?.[0]?.aiDiagnosis || ""
  );
  const textareaRef = useRef(null);
  const aiDiagnosisTextareaRef = useRef(null);

  useEffect(() => {
    if (diagnosisData?.diagnosisList?.[0]?.finalDiagnosis) {
      setDiagnosis(diagnosisData.diagnosisList[0].finalDiagnosis);
    }
    if (diagnosisData?.diagnosisList?.[0]?.aiDiagnosis) {
      setAiDiagnosis(diagnosisData.diagnosisList[0].aiDiagnosis);
    }
  }, [
    diagnosisData?.diagnosisList?.[0]?.finalDiagnosis,
    diagnosisData?.diagnosisList?.[0]?.aiDiagnosis,
  ]);

  // finalDiagnosis textarea 높이 자동 조절
  const adjustFinalDiagnosisHeight = useCallback(() => {
    if (!textareaRef?.current) return;
    const textarea = textareaRef.current;
    const previousHeight = textarea.style.height;
    textarea.style.height = "auto";
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.max(100, scrollHeight);
    if (previousHeight !== `${newHeight}px`) {
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  // aiDiagnosis textarea 높이 자동 조절
  const adjustAiDiagnosisHeight = useCallback(() => {
    if (!aiDiagnosisTextareaRef?.current) return;
    const textarea = aiDiagnosisTextareaRef.current;
    const previousHeight = textarea.style.height;
    textarea.style.height = "auto";
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(500, Math.max(80, scrollHeight));
    if (previousHeight !== `${newHeight}px`) {
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      adjustFinalDiagnosisHeight();
    });
  }, [diagnosis, adjustFinalDiagnosisHeight]);

  useEffect(() => {
    requestAnimationFrame(() => {
      adjustAiDiagnosisHeight();
    });
  }, [aiDiagnosis, adjustAiDiagnosisHeight]);
  //높이 자동 조절 끝

  const handleDiagnosisChange = (e) => {
    const newValue = e.target.value;
    const originalValue = newValue.replace(/\.\n/g, ".").replace(/\n/g, " ");
    setDiagnosis(originalValue);

    if (setResult && result) {
      setResult({
        ...result,
        [position.toLowerCase()]: {
          ...result[position.toLowerCase()],
          diagnosisList: [
            {
              ...result[position.toLowerCase()].diagnosisList[0],
              finalDiagnosis: originalValue,
            },
            ...result[position.toLowerCase()].diagnosisList.slice(1),
          ],
        },
      });
    }

    requestAnimationFrame(() => {
      adjustFinalDiagnosisHeight();
    });
  };

  const handleAiDiagnosisChange = (e) => {
    const newValue = e.target.value;
    const originalValue = newValue.replace(/\.\n/g, ".").replace(/\n/g, " ");
    setAiDiagnosis(originalValue);

    if (setResult && result) {
      setResult({
        ...result,
        [position.toLowerCase()]: {
          ...result[position.toLowerCase()],
          diagnosisList: [
            {
              ...result[position.toLowerCase()].diagnosisList[0],
              aiDiagnosis: originalValue,
            },
            ...result[position.toLowerCase()].diagnosisList.slice(1),
          ],
        },
      });
    }

    requestAnimationFrame(() => {
      adjustAiDiagnosisHeight();
    });
  };

  const getStatusText = (status) => {
    if (status === "GREEN") return "정상";
    if (status === "YELLOW") return "주의";
    return "위험";
  };

  const getStatusIcon = (status) => {
    if (status === "GREEN") return <CheckCircleIcon />;
    if (status === "YELLOW") return <WarningCircleIcon />;
    return <AbnormalCircleIcon />;
  };

  const diagnosisItem = diagnosisData?.diagnosisList?.[0];
  if (!diagnosisItem) return null;

  return (
    <div className="result-header-container-item">
      <div
        className={`result-header-top result-header-top-${diagnosisItem.finalDiagnosisStatus}`}
      >
        <div className="result-header-top-title">
          {position === "LEFT" ? "Left(좌측)" : "Right(우측)"}
        </div>
      </div>
      <div className="result-header-top-content-container">
        <div className="result-header-top-content">
          <div className="result-header-top-content-item">
            <div className="result-header-top-content-item-label">
              종합 소견
            </div>
            <div className={`type type-${diagnosisItem.finalDiagnosisStatus}`}>
              {getStatusIcon(diagnosisItem.finalDiagnosisStatus)}
              {getStatusText(diagnosisItem.finalDiagnosisStatus)}
            </div>
          </div>
          <img
            className="result-header-top-content-item-image"
            src={diagnosisItem.woundPictureUrl}
            alt={`${position.toLowerCase()}-image`}
          />
          <textarea
            ref={textareaRef}
            className="result-header-top-content-item-result"
            value={formatDiagnosisWithLineBreaks(diagnosis)}
            onChange={handleDiagnosisChange}
            readOnly={true}
          />
        </div>
        <div className="result-header-top-content">
          <div className="result-header-top-content-item">
            <div className="result-header-top-content-item-label">
              초음파 스크리닝
            </div>
            <div className={`type type-${diagnosisItem.aiDiagnosisStatus}`}>
              {getStatusIcon(diagnosisItem.aiDiagnosisStatus)}
              {getStatusText(diagnosisItem.aiDiagnosisStatus)}
            </div>
          </div>
          <textarea
            ref={aiDiagnosisTextareaRef}
            className="result-header-top-content-item-result"
            value={formatDiagnosisWithLineBreaks(aiDiagnosis)}
            onChange={handleAiDiagnosisChange}
            readOnly={true}
          />
        </div>
        <div className="result-header-top-content">
          <div className="result-header-top-content-item">
            <div className="result-header-top-content-item-label">
              문진 결과
            </div>
            <div className={`type type-${diagnosisItem.surveyStatus}`}>
              {getStatusIcon(diagnosisItem.surveyStatus)}
              {getStatusText(diagnosisItem.surveyStatus)}
            </div>
          </div>
          <div className="result-header-top-content-item-result-survey">
            {formatSurveyResult(diagnosisItem.survey)?.map((item, index) => (
              <div
                key={index}
                className="result-header-top-content-item-result-survey-item"
              >
                <div className="result-header-top-content-item-result-survey-item-question">
                  {item.question}
                </div>
                <div className="result-header-top-content-item-result-survey-item-answer">
                  {Array.isArray(item.answer)
                    ? item.answer.map((answer, idx) => (
                        <div key={idx}>
                          {answer}
                          {idx < item.answer.length - 1 && <br />}
                        </div>
                      ))
                    : item.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patientCode, setPatientCode] = useState(null);
  const [result, setResult] = useState(null);
  const [surgeryList, setSurgeryList] = useState([]);
  const [selectedSurgeryId, setSelectedSurgeryId] = useState(null);

  useEffect(() => {
    cleanPatientCode();
  }, []);

  const cleanPatientCode = () => {
    if (SecureStorage.getItem("createdPatientCode")) {
      SecureStorage.removeItem("createdPatientCode");
    }
  };

  // surgery 데이터를 result 형식으로 변환하는 함수
  const transformSurgeryToResult = (surgery) => {
    const transformedResult = {};
    if (surgery?.diagnosisList && Array.isArray(surgery.diagnosisList)) {
      surgery.diagnosisList.forEach((item) => {
        const position = item.position?.toLowerCase();
        if (position === "left" || position === "right") {
          transformedResult[position] = {
            diagnosisList: [item],
            insertDate: surgery.insertDate,
            surgeryId: surgery.surgeryId,
          };
        }
      });
    }
    return transformedResult;
  };

  useEffect(() => {
    const getResult = async () => {
      const response = await PatientApi.getPatientInfo({ patientId: id });
      console.log("response ->", response);
      setPatientCode(response.patientCode);

      if (response?.surgeryList && Array.isArray(response.surgeryList)) {
        // surgeryList를 insertDate 기준으로 내림차순 정렬 (최신순)
        const sortedSurgeryList = [...response.surgeryList].sort((a, b) => {
          return new Date(b.insertDate) - new Date(a.insertDate);
        });

        setSurgeryList(sortedSurgeryList);

        // 가장 최신 surgery를 초기 result로 설정
        if (sortedSurgeryList.length > 0) {
          const latestSurgery = sortedSurgeryList[0];
          const transformedResult = transformSurgeryToResult(latestSurgery);
          setResult(transformedResult);
          setSelectedSurgeryId(latestSurgery.surgeryId);
        }
      } else {
        console.warn("surgeryList가 없거나 배열이 아닙니다.");
      }
    };
    getResult();
  }, [id]);

  const formatSurveyResult = (surveyString) => {
    if (!surveyString) return null;

    const answers = surveyString.split(",");
    if (answers.length !== 3) return null;

    const question1Answers = {
      0: "응답 없음",
      1: "전혀 아프지 않다.",
      2: "뻐근한 정도 통증이 있다.",
      3: "일상생활에 지장을 받을 정도 통증이 있다.",
      4: "잠을 못잘 정도 통증이 있다.",
    };

    const question2Answers = {
      0: "응답 없음",
      1: "어깨",
      2: "팔뚝",
      3: "목 · 승모근 · 견갑골 안쪽의 아픔, 손 · 팔의 저림",
    };

    const question3Answers = {
      0: "응답 없음",
      1: "가만히 있어도 아프다.",
      2: "특정 자세나 동작을 할 때만 아프다.",
      3: "어깨를 움직일 때 아프다.",
      4: "목을 움직일 때 아프다.",
    };

    const getAnswers = (answerString, answerMap) => {
      if (!answerString) return ["응답 없음"];
      const numbers = answerString.trim().split(/\s+/).filter(Boolean);
      const answers = numbers
        .map((num) => answerMap[num.trim()] || "응답 없음")
        .filter(Boolean);
      return answers.length > 0 ? answers : ["응답 없음"];
    };

    return [
      {
        question: "통증 정도",
        answer: getAnswers(answers[0], question1Answers),
      },
      {
        question: "통증 부위",
        answer: getAnswers(answers[1], question2Answers),
      },
      {
        question: "통증 유발 동작",
        answer: getAnswers(answers[2], question3Answers),
      },
    ];
  };

  // 날짜 포맷팅
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${ampm} ${hours}:${minutes}:${seconds}`;
  };

  // 마침표 이후 줄 바꿈
  const formatDiagnosisWithLineBreaks = (text) => {
    if (!text) return "";
    return text.replace(/\.\s+/g, ".\n");
  };

  const goToMain = () => {
    navigate("/main");
  };

  const onPatientCreated = (createdPatientCode) => {
    navigate("/main", { state: { createdPatientCode } });
  };

  const [isResultHidden, setIsResultHidden] = useState(false);

  const hideResult = () => {
    setIsResultHidden(!isResultHidden);
  };

  const newSurgery = () => {
    SecureStorage.setItem("createdPatientCode", patientCode);
    navigate("/main");
  };

  // surgery 선택 핸들러
  const handleSurgerySelect = (surgery) => {
    const transformedResult = transformSurgeryToResult(surgery);
    setResult(transformedResult);
    setSelectedSurgeryId(surgery.surgeryId);
    setIsResultHidden(false); // 선택 시 내용을 보여주기 위해 false로 변경
  };

  return (
    <>
      <div className="main">
        <Header />
        <div className="main-container">
          <PatientList
            goToMain={goToMain}
            onPatientCreated={onPatientCreated}
          />
          <div className="home-container">
            <div className="patient-info-container">
              <AccountCircleIcon alt="account-circle" />
              <div className="patient-code">{patientCode}</div>
            </div>
            {isResultHidden && (
              <div>
                <div
                  style={{
                    cursor: "pointer",
                    width: "200px",
                    height: "50px",
                    backgroundColor: "#2563eb",
                    color: "#fff",
                    borderRadius: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "16px",
                    fontWeight: "700",
                  }}
                  onClick={newSurgery}
                >
                  시술 생성하기
                </div>
              </div>
            )}
            {isResultHidden ? (
              <>
                <div className="surgery-list-container">
                  {surgeryList.map((surgery) => (
                    <div
                      key={surgery.surgeryId}
                      className={`surgery-box ${
                        selectedSurgeryId === surgery.surgeryId
                          ? "selected"
                          : ""
                      }`}
                      onClick={() => handleSurgerySelect(surgery)}
                    >
                      <div className="surgery-box-header">
                        <div className="surgery-box-left-container">
                          <div className="surgery-box-left">
                            <ListIcon
                              alt="list-icon"
                              className="result-header-icon"
                            />
                            <div className="result-header-title">
                              AI 진단 보조 결과 리포트
                            </div>
                          </div>
                          <div className="result-header-date">
                            <ResultCalendarMonth />
                            {formatDate(surgery.insertDate)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="container container-result">
                <div className="result-header">
                  <div className="result-header-left-container">
                    <div className="result-header-left">
                      <ListIcon
                        alt="list-icon"
                        className="result-header-icon"
                      />
                      <div className="result-header-title">
                        AI 진단 보조 결과 리포트
                      </div>
                    </div>
                    <div className="result-header-date">
                      <ResultCalendarMonth />
                      {result?.left?.insertDate
                        ? formatDate(result.left.insertDate)
                        : ""}
                    </div>
                  </div>
                  <div
                    className="result-header-toggle"
                    onClick={() => {
                      hideResult();
                    }}
                  >
                    <BackArrow />
                  </div>
                </div>
                <div className={`result-header-container`}>
                  {result?.left && (
                    <DiagnosisResultItem
                      position="LEFT"
                      diagnosisData={result.left}
                      result={result}
                      setResult={setResult}
                      formatSurveyResult={formatSurveyResult}
                      formatDiagnosisWithLineBreaks={
                        formatDiagnosisWithLineBreaks
                      }
                    />
                  )}
                  {result?.right && (
                    <DiagnosisResultItem
                      position="RIGHT"
                      diagnosisData={result.right}
                      result={result}
                      setResult={setResult}
                      formatSurveyResult={formatSurveyResult}
                      formatDiagnosisWithLineBreaks={
                        formatDiagnosisWithLineBreaks
                      }
                    />
                  )}
                </div>
                <div className="result-footer-wrapper">
                  <Footer />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResultPage;
